import VueRouter from "vue-router";
import {Store} from "vuex";
import {PathToProduct} from "@/enum/Product";
import {ProgramPaths} from "@/enum/ProgramPaths";
import {LisansDurum, rotaOzelligi} from "@/utils/lisansKisit";

/**
 * DEV-2113 — açık olan sayfa lisans ya da özellik yüzünden kapandığında kullanıcıyı
 * o sayfada bırakmamak.
 *
 * Router muhafızı (router/index.ts, kullaniciIzinKontrol) kararı yalnız sayfaya
 * GİRERKEN verir. Kullanıcı içerideyken lisans biterse ya da özellik büroya kapatılırsa
 * sayfa açık kalıyordu: istekler reddediliyor, liste "takip bulunamadı" gösteriyordu
 * (veri kaybı gibi okunur). Özet her tazelendiğinde (bitiş anındaki zamanlayıcı, lisans
 * 403'ü, büro değişimi) açık sayfa bu dosyadaki kuralla yeniden denetlenir.
 */

/**
 * Sayfanın ait olduğu ürünün yol parçası ('icrapro' gibi). Lisans ve "aktif değil"
 * ekranlarında ürün, gelinen ürünü taşıyan query'den okunur; ürüne ait olmayan sayfada
 * ürünün değil sayfanın ilk parçası döner (eşleşen ürün yoktur).
 */
export function sayfaUrunParcasi(yol: string, urunSorgusu?: unknown): string {
    if (yol === ProgramPaths.lisansDurumu || yol === ProgramPaths.ozellikKapali) {
        return String(urunSorgusu ?? "").replace(/^\//, "");
    }
    return yol.split("/")[1] ?? "";
}

/** Kararın sorduğu üç şey; router'da Helper'dan, testte sahteden gelir. */
export interface SayfaErisimi {
    ekranlarAcikMi(urun: number): boolean;
    lisansDurumu(urun: number): LisansDurum;
    ozellikAcikMi(anahtar: string): boolean;
}

/**
 * Açık sayfa artık girilemiyorsa gidilecek yer; girilebiliyorsa null. Muhafızın kararıyla
 * aynı sıra:
 *  - ürünün ekranları kapandıysa (süresi bitti ya da lisans yok) → lisans ekranı;
 *  - sayfa bir özelliğe bağlıysa ve özellik bu büroda kapandıysa → "aktif değil" ekranı.
 * Kısıtlı dönem sayfayı kapatmaz (okuma serbest). Yetki burada sorulmaz; o sayfa
 * geçişinde denetlenir. Ürüne ait olmayan sayfada (lisans ekranı, giriş) karar yoktur.
 */
export function kapananSayfaHedefi(
    yol: string,
    metalar: Array<Record<string, unknown> | null | undefined>,
    erisim: SayfaErisimi
): {path: string; query: {urun: string}} | null {
    const parca = yol.split("/")[1] ?? "";
    // @ts-ignore -- PathToProduct string anahtarlı enum; sayı anahtarı geriye ad döner
    const urun: unknown = PathToProduct[parca];
    if (typeof urun !== "number") return null;

    if (!erisim.ekranlarAcikMi(urun)) {
        const durum = erisim.lisansDurumu(urun);
        return durum === "sure_bitti" || durum === "yok"
            ? {path: ProgramPaths.lisansDurumu, query: {urun: parca}}
            : null;
    }

    const ozellik = rotaOzelligi(metalar);
    if (ozellik !== null && !erisim.ozellikAcikMi(ozellik)) {
        return {path: ProgramPaths.ozellikKapali, query: {urun: parca}};
    }

    return null;
}

/**
 * Özet her değiştiğinde açık sayfayı yeniden denetler; kapandıysa kullanıcıyı gideceği
 * ekrana `replace` ile götürür (geri tuşu kapanan sayfaya döndürmesin). Özet yoksa
 * (çıkış, üretilemedi) ya da oturum yoksa hiçbir şey yapmaz. İzlemeyi bırakan
 * fonksiyonu döndürür.
 */
export function kapananSayfadanCik(router: VueRouter, store: Store<any>, erisim: SayfaErisimi): () => void {
    return store.watch(
        (_state, getters) => getters.ozellikDurumu,
        (yeni) => {
            if (!yeni || !store.getters.accessToken) return;
            const rota = router.currentRoute;
            const hedef = kapananSayfaHedefi(rota.path, rota.matched.map((kayit) => kayit.meta), erisim);
            if (hedef) router.replace(hedef).catch(() => undefined);
        }
    );
}
